<template>
  <RevStickyBar
    id="sticky-bar-top-small"
    class="block md:hidden [@media(max-height:512px)]:hidden"
    :class="{ '!px-12': isChristmasTime }"
    position="top"
    :visible="showBar"
  >
    <div ref="topBarSmallRef">
      <div v-if="!isChristmasTime" class="caption-bold py-4">
        <div>
          <template v-for="(item, index) in selectedTitle" :key="item.value">
            <div
              v-if="upgradePickerFields && item.upgraded"
              class="relative inline-block"
              :class="[$style.shimmerText]"
            >
              <span
                :key="item.value"
                class="rounded-md inline-block text-transparent"
                :class="{
                  [$style.animateShimmer]: upgradePickerFields && item.upgraded,
                }"
              >
                {{ item.value }}
              </span>
            </div>

            <span
              v-if="!(upgradePickerFields && item.upgraded)"
              class="rounded-md inline-block"
            >
              {{ item.value }}
            </span>
            <span v-if="index !== selectedTitle.length - 1" class="mx-4">
              -
            </span>
          </template>
        </div>
      </div>
      <div v-else class="flex items-center gap-12">
        <div class="shrink-0">
          <RevIllustration
            v-if="hasDeliveryBeforeChristmas"
            alt=""
            :height="36"
            src="/img/product/delivery-before-christmas.svg"
            :width="36"
          />
          <IconTruckExpress v-else />
        </div>
        <ShippingBlock :shipping />
      </div>
    </div>

    <slot name="progress-bar" />
  </RevStickyBar>
</template>

<script lang="ts" setup>
import { type ComputedRef, computed, useTemplateRef } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { RevIllustration } from '@ds/components/Illustration'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'

import { useForceHideHeader } from '~/scopes/product/components/CustomizationFunnel/composables/useForceHideHeader/useForceHideHeader'
import { useDeliveryBeforeChristmas } from '~/scopes/product/composables/useDeliveryBeforeChristmas'
import ShippingBlock from '~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemShipping/components/ShippingBlock/ShippingBlock.vue'

import { useShowStickyBarTopSmall } from './composables/useShowStickyBarTopSmall'

const props = defineProps<{
  selectedOptions: string[]
  upgradePickerFields?: NonNullable<
    GetPickersResponse['upgrade']
  >['pickerFields']
  shipping: NonNullable<GetPickersResponse['selectedOffer']>['shipping']
}>()

const { isChristmasTime, hasDeliveryBeforeChristmas } =
  useDeliveryBeforeChristmas(props.shipping)

const { showBar } = useShowStickyBarTopSmall()

// hides/shows the navigation header depending on the
// visibility (rendering state) of the sticky bar.
// RevStickyBar destroys its DOM childs when not visible
// so we can use it to determine if the main header should be hidden
const topBarSmallRef = useTemplateRef('topBarSmallRef')
useForceHideHeader(topBarSmallRef)

const selectedTitle: ComputedRef<
  {
    value: string
    upgraded: boolean
  }[]
> = computed(() => {
  if (!props.upgradePickerFields) {
    return props.selectedOptions.map((option) => ({
      value: option,
      upgraded: false,
    }))
  }

  const upgradedFields = Object.values(props.upgradePickerFields)

  return props.selectedOptions.map((option, index) => {
    const upgradedField = upgradedFields[index]

    return {
      value: props.upgradePickerFields ? upgradedField.value : option,
      upgraded: upgradedField.upgrade,
    }
  })
})
</script>

<style scoped module>
.shimmerText {
  background-image: linear-gradient(to right, #4055bf 50%, #2f3137 50%);
  background-size: 200% 100%;
  background-position: 100% 0;
  background-clip: text;
  animation: color-change-animation 2s forwards;
}

@keyframes color-change-animation {
  0% {
    background-position: 100% 0;
    @apply text-static-default-hi;
  }

  100% {
    background-position: 0 0;
    @apply text-static-brand-hi;
  }
}
</style>
